import { useCallback, useRef, useState } from 'react';
import { STORAGE_KEY_PLAYED_VIDEOS, STORAGE_KEY_PLAYED_NARRATIONS } from '../constants';

type LastFrameData = { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null;

/**
 * シーンkeyで索引付けされた状態群を一括管理するフック。
 * swapKeys / deleteKey でシーン並べ替え・削除時の状態更新を一発で行える。
 */
export function useSceneState() {
  const [scenePreviews, setScenePreviews] = useState<Record<string, string[]>>({});
  const [scenePreviewIndex, setScenePreviewIndex] = useState<Record<string, number>>({});
  const [scenePreviewMaterials, setScenePreviewMaterials] = useState<Record<string, any[]>>({});
  const [scenePreviewLoading, setScenePreviewLoading] = useState<Record<string, boolean>>({});
  const [scenePreviewGenerated, setScenePreviewGenerated] = useState<Record<string, boolean>>({});
  const [lastFrameLoading, setLastFrameLoading] = useState<Record<string, boolean>>({});
  const [sceneVideoLoading, setSceneVideoLoading] = useState<Record<string, boolean>>({});
  const [sceneVideoGenerated, setSceneVideoGenerated] = useState<Record<string, boolean>>({});
  const [sceneVideoMaterialId, setSceneVideoMaterialId] = useState<Record<string, string>>({});
  const [sceneVideoError, setSceneVideoError] = useState<Record<string, string>>({});
  const [confirmedFirstFrames, setConfirmedFirstFrames] = useState<Record<string, string>>({});

  const [sceneLastFrames, _setSceneLastFrames] = useState<Record<string, LastFrameData>>({});
  const sceneLastFramesRef = useRef(sceneLastFrames);
  const setSceneLastFrames: typeof _setSceneLastFrames = (action) => {
    _setSceneLastFrames((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      sceneLastFramesRef.current = next;
      return next;
    });
  };

  const [playedVideos, setPlayedVideos] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PLAYED_VIDEOS);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [playedNarrations, setPlayedNarrations] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PLAYED_NARRATIONS);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  /** 2つのシーンkey間で全状態を一括スワップ */
  const swapKeys = useCallback((keyA: string, keyB: string) => {
    const swapRecord = <T,>(setter: React.Dispatch<React.SetStateAction<Record<string, T>>>) => {
      setter((prev) => {
        const next = { ...prev };
        const tmp = next[keyA];
        next[keyA] = next[keyB];
        next[keyB] = tmp;
        return next;
      });
    };

    swapRecord(setScenePreviews);
    swapRecord(setScenePreviewMaterials);
    swapRecord(setScenePreviewIndex);
    swapRecord(setConfirmedFirstFrames);
    swapRecord(setSceneLastFrames);
    swapRecord(setSceneVideoGenerated);
    swapRecord(setSceneVideoMaterialId);
    swapRecord(setSceneVideoLoading);
    swapRecord(setSceneVideoError);
    swapRecord(setScenePreviewGenerated);
    swapRecord(setScenePreviewLoading);

    setPlayedVideos((prev) => {
      const next = new Set(prev);
      const hadA = prev.has(keyA);
      const hadB = prev.has(keyB);
      if (hadB) next.add(keyA); else next.delete(keyA);
      if (hadA) next.add(keyB); else next.delete(keyB);
      return next;
    });
    setPlayedNarrations((prev) => {
      const next = new Set(prev);
      const hadA = prev.has(keyA);
      const hadB = prev.has(keyB);
      if (hadB) next.add(keyA); else next.delete(keyA);
      if (hadA) next.add(keyB); else next.delete(keyB);
      return next;
    });
  }, []);

  /** 指定keyの全状態を削除 */
  const deleteKey = useCallback((key: string) => {
    const deleteFromRecord = <T,>(setter: React.Dispatch<React.SetStateAction<Record<string, T>>>) => {
      setter((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    };

    deleteFromRecord(setScenePreviews);
    deleteFromRecord(setScenePreviewMaterials);
    deleteFromRecord(setScenePreviewIndex);
    deleteFromRecord(setConfirmedFirstFrames);
    deleteFromRecord(setSceneLastFrames);
    // 動画まわりのセッション状態も消す。残すと、同じ order のシーンを削除→再追加した際に
    // 古い動画素材IDが復活し、新しい素材ではなく前の動画が表示されてしまう。
    deleteFromRecord(setSceneVideoMaterialId);
    deleteFromRecord(setSceneVideoGenerated);
    deleteFromRecord(setSceneVideoLoading);
    deleteFromRecord(setSceneVideoError);
  }, []);

  /**
   * 「すべて開く / すべて閉じる」の合図。シーンカードの折り畳み状態は各 SceneItem が
   * ローカルに持つため (ストーリーボードモードの既定=畳む)、version を進めるたびに
   * 配下の全カードが collapsed の値に揃う。version 0 はマウント時の既定値を尊重する。
   */
  const [sceneCollapseSignal, setSceneCollapseSignal] = useState<{ version: number; collapsed: boolean }>({ version: 0, collapsed: false });
  const setAllScenesCollapsed = useCallback((collapsed: boolean) => {
    setSceneCollapseSignal((prev) => ({ version: prev.version + 1, collapsed }));
  }, []);

  /** 全状態をクリア */
  const clearAll = useCallback(() => {
    setScenePreviews({});
    setScenePreviewIndex({});
    setScenePreviewMaterials({});
    setScenePreviewLoading({});
    setScenePreviewGenerated({});
    setLastFrameLoading({});
    setSceneVideoLoading({});
    setSceneVideoGenerated({});
    setSceneVideoMaterialId({});
    setSceneVideoError({});
    setConfirmedFirstFrames({});
    setSceneLastFrames({});
    setPlayedVideos(new Set());
    setPlayedNarrations(new Set());
  }, []);

  return {
    // States
    scenePreviews, setScenePreviews,
    scenePreviewIndex, setScenePreviewIndex,
    scenePreviewMaterials, setScenePreviewMaterials,
    scenePreviewLoading, setScenePreviewLoading,
    scenePreviewGenerated, setScenePreviewGenerated,
    lastFrameLoading, setLastFrameLoading,
    sceneVideoLoading, setSceneVideoLoading,
    sceneVideoGenerated, setSceneVideoGenerated,
    sceneVideoMaterialId, setSceneVideoMaterialId,
    sceneVideoError, setSceneVideoError,
    confirmedFirstFrames, setConfirmedFirstFrames,
    sceneLastFrames, setSceneLastFrames, sceneLastFramesRef,
    playedVideos, setPlayedVideos,
    playedNarrations, setPlayedNarrations,
    sceneCollapseSignal,
    // Actions
    swapKeys,
    deleteKey,
    clearAll,
    setAllScenesCollapsed,
  };
}
